import {
  useResourceSuspense,
  useUnassignedFiles,
  type ResourceRequest,
  type ResourceResponse
} from "@swivu/client-api";
import React, { createContext, useContext, useState } from "react";
import { EntityChecklists } from "@swivu/entity-detail/src/Checklists.js";
import { EntityContacts } from "@swivu/entity-detail/src/Contacts.js";
import { EntityFiles } from "@swivu/entity-detail/src/Files.js";
import { EntityNotes } from "@swivu/entity-detail/src/notes/Notes.js";
import { EntityReminders } from "@swivu/entity-detail/src/Reminders.js";
import SuspenseWithDelay from "@swivu/shared-ui/src/components/SuspenseWithDelay.js";
import ErrorBoundary from "@swivu/shared-ui/src/components/ErrorBoundary.js";
import {
  AddMediaModal,
  cn,
  decodeFA,
  LOUD_CHIP,
  META_LABEL,
  RichText,
  tagTone,
  type FileData
} from "@swivu/shared-ui";

type ResourceVariables = ResourceRequest;
type ResourceProps = ResourceVariables & {
  resource: ResourceResponse;
  timezone: string;
  baseUrls: {
    checklist: string;
  };
  boardId?: number;
  canAddMedia?: boolean;
  onAnalyze?: (file: FileData) => void;
  advisorsEnabled?: boolean;
};
const ResourceContext = createContext<ResourceProps | null>(null);

type ProviderProps = ResourceVariables & {
  children: React.ReactNode;
  timezone: string;
  baseUrls: {
    checklist: string;
  };
  boardId?: number;
  canAddMedia?: boolean;
  onAnalyze?: (file: FileData) => void;
  advisorsEnabled?: boolean;
};

const ResourceProviderWrapper = (props: ProviderProps) => {
  return (
    <SuspenseWithDelay delay={1000} fallback={<ResourceDetailSkeleton />}>
      <ErrorBoundary>
        <ResourceContextProvider {...props} />
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

const ResourceContextProvider = ({ children, ...props }: ProviderProps) => {
  const { data: resource } = useResourceSuspense({
    variables: props
  });
  return (
    <ResourceContext.Provider
      value={{ ...props, resource, boardId: resource.board }}
    >
      {children}
    </ResourceContext.Provider>
  );
};

const useResource = () => {
  const context = useContext(ResourceContext);
  if (!context) {
    throw new Error("useResource must be used within a ResourceProvider");
  }
  return context;
};

const ResourceDetail = () => {
  const { resource } = useResource();
  const teamColor = resource.team_color || "#999999";

  return (
    <div className="max-w-full">
      {resource.team_name && (
        <div className="mb-2 flex items-center gap-2">
          {resource.team_color && (
            <span
              className="h-2 w-2 rounded-full shadow-[inset_0_1px_0_0_rgba(255,255,255,0.4)]"
              style={{ backgroundColor: resource.team_color }}
            />
          )}
          <span className={META_LABEL}>{resource.team_name}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-surface-xl bg-white dark:bg-mono-900 shadow-card-deep">
        <div
          className="tile-color-strip h-2.5"
          style={{ backgroundColor: teamColor }}
        />
        <div className="mobile-pref-card p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-4">
              {resource.image_url && (
                <img
                  src={resource.image_url}
                  alt={
                    resource.label || resource.display_name || "Resource image"
                  }
                  className="h-16 w-16 flex-none rounded-surface-xs bg-white object-cover shadow-sunken"
                />
              )}
              <div className="min-w-0">
                <h2 className="mobile-pref-section-title text-2xl leading-tight font-black text-mono-900 dark:text-white">
                  {resource.label ||
                    resource.display_name ||
                    "Untitled Resource"}
                </h2>
                {resource.location && (
                  <div className="mt-1 flex items-start gap-2">
                    <svg
                      className="mobile-pref-detail-inline-icon mt-0.5 flex-shrink-0 text-mono-500 dark:text-mono-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <span className="mobile-pref-card-body text-sm font-black text-mono-600 dark:text-mono-300">
                      {resource.location}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {resource.status_value && (
              <span className={cn(LOUD_CHIP, "bg-tile-primary")}>
                {resource.status_value}
              </span>
            )}
          </div>

          {resource.description && (
            <div className="py-4">
              <RichText
                className="mobile-pref-rich-text font-body text-mono-700 dark:text-mono-300 text-sm"
                html={resource.description}
              />
            </div>
          )}

          <div className="mobile-pref-card-body flex flex-wrap gap-x-6 gap-y-4 py-2 text-sm">
            {resource.team_name && (
              <div className="flex flex-col gap-1">
                <span className={META_LABEL}>Team</span>
                <span
                  className={cn(
                    LOUD_CHIP,
                    "self-start",
                    !resource.team_color && tagTone(resource.team_name)
                  )}
                  style={
                    resource.team_color
                      ? { backgroundColor: resource.team_color }
                      : undefined
                  }
                >
                  {resource.team_name}
                </span>
              </div>
            )}

            <ResourceAttribute
              title="Category"
              value={resource.category_value}
            />
            <ResourceAttribute title="Type" value={resource.type_description} />
            <ResourceAttribute title="Job Title" value={resource.job_title} />
          </div>

          <LinkedResources />

          {(resource.email || resource.phone) && (
            <div className="mt-4 pt-4">
              <h4 className={cn("mb-3", META_LABEL)}>Contact Information</h4>
              <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                {resource.email && (
                  <ContactRow
                    href={`mailto:${resource.email}`}
                    label={resource.email}
                    tone="bg-tile-info"
                    icon={
                      <>
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </>
                    }
                  />
                )}
                {resource.phone && (
                  <ContactRow
                    href={`tel:${resource.phone}`}
                    label={resource.phone}
                    tone="bg-tile-success"
                    icon={
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    }
                  />
                )}
              </div>
            </div>
          )}

          {resource.tag_values && resource.tag_values.length > 0 && (
            <div className="my-2">
              <div className="flex flex-wrap gap-2">
                {resource.tag_values
                  .filter((t): t is string => !!t)
                  .map((tag, index) => (
                    <span key={index} className={cn(LOUD_CHIP, tagTone(tag))}>
                      {tag}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ResourceAttribute = ({
  title,
  value
}: {
  title: string;
  value?: string | null;
}) => {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className={META_LABEL}>{title}</span>
      <span className={cn(LOUD_CHIP, "self-start", tagTone(value))}>
        {value}
      </span>
    </div>
  );
};

const ContactRow = ({
  href,
  label,
  tone,
  icon
}: {
  href: string;
  label: string;
  tone: string;
  icon: React.ReactNode;
}) => {
  return (
    <a
      href={href}
      className={cn(
        "mobile-pref-card-body inline-flex h-12 min-w-0 items-center gap-2 rounded-full px-4 text-base font-black",
        "shadow-pill text-shadow-on-color-strong text-white",
        tone,
        "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.97]",
        "motion-reduce:transform-none motion-reduce:transition-none",
        "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
      )}
    >
      <svg
        className="mobile-pref-detail-inline-icon flex-none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {icon}
      </svg>
      <span className="truncate">{label}</span>
    </a>
  );
};

const LinkedResources = () => {
  const {
    resource,
    boardId,
    canAddMedia,
    onAnalyze,
    advisorsEnabled,
    ...props
  } = useResource();
  const [showAddMedia, setShowAddMedia] = useState(false);

  const { data: unassignedFiles, isLoading: isLoadingUnassigned } =
    useUnassignedFiles({
      enabled: !!(canAddMedia && !!boardId && props.api === "wf")
    });

  const variables = {
    ...props,
    id: typeof props.id === "string" ? parseInt(props.id, 10) : props.id,
    entity_type: "resource" as const,
    title: resource.display_name ?? ""
  };

  const canAdd = canAddMedia && !!boardId && resource.can_create_file !== false;
  return (
    <>
      <div className="has-[&>*]:py-4 grid w-full grid-cols-2 gap-4 md:grid-cols-3 md:gap-12">
        <EntityContacts {...variables} />
        {resource?.notes_count && resource?.notes_count > 0 ? (
          <EntityNotes {...variables} />
        ) : null}
        {resource?.checklist_count && resource?.checklist_count > 0 ? (
          <EntityChecklists {...variables} baseUrl={props.baseUrls.checklist} />
        ) : null}
        {resource?.files_count && resource?.files_count > 0 ? (
          <EntityFiles
            {...variables}
            boardId={boardId}
            canAdd={canAdd}
            onAdd={() => setShowAddMedia(true)}
            onAnalyze={onAnalyze}
            advisorsEnabled={advisorsEnabled}
          />
        ) : (
          canAdd && (
            <button
              type="button"
              onClick={() => setShowAddMedia(true)}
              aria-label="Add media"
              className={cn(
                "shadow-raised text-shadow-on-color-strong",
                "relative flex aspect-square w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-surface-tile px-4 py-5 text-white",
                "bg-tile-media",
                "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
                "motion-reduce:transform-none motion-reduce:transition-none",
                "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
              )}
            >
              <span aria-hidden className="top-gloss-large" />
              <i
                className={cn(
                  "relative text-6xl flex-none fa-regular",
                  decodeFA("photo-film-music")
                )}
              />
              <span className="relative text-center text-2xl font-black leading-[1.15] tracking-tight">
                Add Media
              </span>
            </button>
          )
        )}
        <EntityReminders {...variables} timezone={props.timezone} />
      </div>

      {canAdd && (
        <AddMediaModal
          entity={{
            type: "resource",
            id: variables.id,
            name: resource.display_name ?? "Resource"
          }}
          boardId={boardId}
          open={showAddMedia}
          onOpenChange={setShowAddMedia}
          unassignedFiles={unassignedFiles}
          isLoadingUnassigned={isLoadingUnassigned}
        />
      )}
    </>
  );
};

const ResourceDetailSkeleton = () => {
  return (
    <div
      className="max-w-full animate-pulse"
      role="status"
      aria-busy
      aria-live="polite"
    >
      <div className="overflow-hidden rounded-surface-xl bg-white dark:bg-mono-900 shadow-card-deep">
        <div className="bg-mono-200 dark:bg-mono-700 h-2.5" />

        <div className="space-y-4 p-6">
          <div className="flex items-start justify-between">
            <div className="bg-mono-200 dark:bg-mono-700 h-7 w-48 rounded-surface-xs" />
            <div className="bg-mono-200 dark:bg-mono-700 h-11 w-24 rounded-full" />
          </div>

          <div className="flex gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <div className="bg-mono-200 dark:bg-mono-700 h-3 w-16 rounded-surface-2xs" />
                <div className="bg-mono-200 dark:bg-mono-700 h-11 w-24 rounded-full" />
              </div>
            ))}
          </div>

          <div className="bg-mono-200 dark:bg-mono-700 h-20 rounded-surface-xs" />

          <div className="bg-mono-200 dark:bg-mono-700 h-6 w-72 rounded-surface-xs" />

          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-mono-100 dark:bg-mono-800 flex flex-col gap-1.5 p-2 rounded-surface-lg"
              >
                <div className="bg-mono-200 dark:bg-mono-700 h-6 w-8 rounded-surface-2xs" />
                <div className="bg-mono-200 dark:bg-mono-700 h-3 w-16 rounded-surface-2xs" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

ResourceDetail.Provider = ResourceProviderWrapper;
ResourceDetail.useResource = useResource;
ResourceDetail.Skeleton = ResourceDetailSkeleton;
export default ResourceDetail;
